import BigNumber from "bignumber.js";

BigNumber.config({ EXPONENTIAL_AT: [-8, 8] });

export function formatNumber(num: number, precision = 2) {
  const map = [
    { suffix: "T", threshold: 1e12 },
    { suffix: "B", threshold: 1e9 },
    { suffix: "M", threshold: 1e6 },
    { suffix: "K", threshold: 1e3 },
    { suffix: "", threshold: 1 },
  ];

  const found = map.find((x) => Math.abs(num) >= x.threshold);
  if (found) {
    const formatted = (num / found.threshold).toFixed(precision) + found.suffix;
    return formatted;
  }

  return num;
}

export const truncateString = (str: string, maxLength = 10) => {
  if (!str) {
    return str;
  }

  const data = str.split(".");
  if (data[0].length > maxLength) {
    return `${str.substring(0, maxLength)}e+${data[0].length - maxLength}`;
  } else {
    return str.length > maxLength ? `${str.substring(0, maxLength)}` : str;
  }
};

export const toFixedString = (str: BigNumber | undefined, maxLength = 2) => {
  if (!str || str?.toString() === "0") {
    return str?.toString();
  }

  const data = str.toString().split(".");
  const match = str.toString().match(/e\+\d*$/g);

  if (data[1]?.length > maxLength) {
    return `${new BigNumber(data[0]).toFormat()}.${
      data[1].substring(0, maxLength) || 0
    }${match?.[0] || ""}`;
  } else {
    return str?.toFormat(maxLength).replace(/\.?0+$/, "");
  }
};

/**
 * 移除数字字符串尾随的零和小数点
 * @param numStr - 数字字符串（如 "10.000000000000000000"）
 * @returns 清理后的字符串（如 "10"）
 */
export const removeTrailingZeros = (
  numStr: string | number | undefined | null,
): string => {
  if (numStr === undefined || numStr === null) {
    return "";
  }

  const str = String(numStr);

  // 如果字符串不包含小数点，直接返回
  if (!str.includes(".")) {
    return str;
  }

  // 移除尾随的零和小数点
  return str.replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "");
};

/**
 * 格式化 BigNumber 对象，移除尾随零
 * @param value - BigNumber 对象
 * @param decimals - 小数位数
 * @returns 格式化后的字符串
 */
export const formatBigNumberWithoutTrailingZeros = (
  value: BigNumber | string | number,
  decimals?: number,
): string => {
  const bn = BigNumber.isBigNumber(value) ? value : new BigNumber(value);

  if (bn.isNaN() || !bn.isFinite()) {
    return "0";
  }

  // 如果指定了小数位数，使用 toFixed
  const formatted =
    decimals !== undefined ? bn.toFixed(decimals) : bn.toString();

  return removeTrailingZeros(formatted);
};

/**
 * 格式化小数值，只对 "0.0000...123" 这种前面 0 过多的小数使用省略号格式
 * 例如: "0.00000000106" -> "0.00...106"
 * 其他情况（如 "123.45678901"）保持原值不变
 * @param numStr - 数字字符串
 * @param maxLength - 最大显示长度（默认 8）
 * @returns 格式化后的字符串
 */
export const formatWithEllipsis = (
  numStr: string | number | undefined | null,
  maxLength = 8,
): string => {
  if (numStr === undefined || numStr === null) {
    return "";
  }

  const str = removeTrailingZeros(String(numStr));

  // 如果长度小于等于最大长度，直接返回
  if (str.length <= maxLength) {
    return str;
  }

  // 如果不包含小数点，直接返回原值（整数情况保持不变）
  if (!str.includes(".")) {
    return str;
  }

  // 处理小数情况
  const [intPart, decPart] = str.split(".");

  // 整数部分不为 0 时，最多保留 4 位小数
  if (intPart !== "0") {
    if (decPart.length <= 4) {
      return str;
    }
    return removeTrailingZeros(`${intPart}.${decPart.substring(0, 4)}`);
  }

  // 找出小数部分开头有多少个 0
  const leadingZerosMatch = decPart.match(/^0+/);
  const leadingZerosCount = leadingZerosMatch ? leadingZerosMatch[0].length : 0;

  // 前导 0 小于等于 6 个时，保留 8 位小数
  if (leadingZerosCount <= 6) {
    if (decPart.length <= 8) {
      return str;
    }
    return removeTrailingZeros(`${intPart}.${decPart.substring(0, 8)}`);
  }

  // 找出小数部分的有效数字（非零开始的部分）
  const significantPart = decPart.substring(leadingZerosCount);
  if (!significantPart) {
    return "0";
  }

  // 保留格式: "0." + "00" + "..." + 最后几位有效数字
  // 例如: "0.00...106"
  const prefixZeros = Math.min(2, leadingZerosCount); // 最多显示2个前导0
  const suffixLength = Math.min(3, significantPart.length); // 最多显示3位有效数字
  const suffix = significantPart.substring(0, suffixLength);

  return `${intPart}.${"0".repeat(prefixZeros)}...${suffix}`;
};

/**
 * 智能格式化 USD 价值显示
 * 根据数值大小自动选择合适的精度，并移除尾随零
 * @param usdValue - BigNumber USD 价值
 * @param customAccuracy - 自定义精度（可选）
 * @returns 格式化后的字符串
 */
export const formatUsdValue = (
  usdValue: BigNumber,
  customAccuracy?: number,
): string => {
  // 如果指定了自定义精度，使用它
  if (customAccuracy !== undefined) {
    return removeTrailingZeros(usdValue.toFixed(customAccuracy));
  }

  // 智能选择精度
  const absValue = usdValue.abs();

  // 非常小的值 (< $0.01)：显示最多 8 位小数，但去除尾随零
  if (absValue.isLessThan(0.01) && !absValue.isZero()) {
    // 找到第一个非零小数位
    let precision = 2;
    let tempValue = absValue;
    while (tempValue.isLessThan(0.01) && precision < 8) {
      precision++;
      tempValue = tempValue.multipliedBy(10);
    }
    // 至少显示到第一个有效数字后 2 位
    precision = Math.min(precision + 2, 8);
    return removeTrailingZeros(usdValue.toFixed(precision));
  }

  // 小值 (< $1)：显示 4 位小数
  if (absValue.isLessThan(1)) {
    return removeTrailingZeros(usdValue.toFixed(4));
  }

  // 中等值 (< $1000)：显示 2 位小数
  if (absValue.isLessThan(1000)) {
    return removeTrailingZeros(usdValue.toFixed(2));
  }

  // 大值 (>= $1000)：显示 2 位小数
  return removeTrailingZeros(usdValue.toFixed(2));
};
